/**
 * RoleSelectionModal — mandatory one-time onboarding step.
 *
 * Shown immediately after login when the user has not yet selected a role.
 * Cannot be dismissed or skipped — role selection is required for RBAC assignment.
 *
 * Design rules (from spec):
 *   - Non-dismissible: no backdrop click, no Escape key
 *   - Blocks all dashboard usage until completed
 *   - Only INDIVIDUAL and PARTNER are selectable (never ADMIN/SUPPORT)
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { selectRole } from '@/src/modules/auth/session';
import { useAuthStore } from '@/src/modules/auth/useAuthStore';
import { cn } from '@/src/infrastructure/lib/utils';

type RoleOption = 'INDIVIDUAL' | 'PARTNER';

const ROLE_OPTIONS: Array<{
  value: RoleOption;
  label: string;
  description: string;
  icon: React.ReactNode;
}> = [
  {
    value: 'INDIVIDUAL',
    label: 'Personal Invitations',
    description:
      'Create and share beautiful invitations for weddings, birthdays, and personal events.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-6 h-6"
      >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
  {
    value: 'PARTNER',
    label: 'Event Organizer / Photographer',
    description:
      'Manage multiple events, access analytics, and showcase your brand with partner features.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-6 h-6"
      >
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
        <path d="M7 8l3 3 4-4 3 3" />
      </svg>
    ),
  },
];

export function RoleSelectionModal({ open }: { open: boolean }) {
  const [selected, setSelected] = useState<RoleOption | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCurrentUser = useAuthStore((s) => s.loadCurrentUser);

  async function handleContinue() {
    if (!selected || busy) return;
    setBusy(true);
    setError(null);
    try {
      await selectRole(selected);
      // Reload the RBAC snapshot — this clears roleSelectionRequired and
      // hydrates the correct permissions for the chosen role.
      await loadCurrentUser();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      );
      setBusy(false);
    }
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Non-clickable backdrop — intentionally does NOT close modal */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Select your role"
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: 'spring', stiffness: 300, damping: 26 }}
            className="relative w-full max-w-lg rounded-[2rem] bg-surface-lowest p-8 shadow-2xl border border-outline-variant/20"
          >
            {/* Header */}
            <div className="mb-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-7 h-7 text-primary"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-on-surface">
                Welcome to InviteCloud
              </h2>
              <p className="text-sm text-on-surface-variant mt-1.5">
                How will you use InviteCloud? This helps us personalise your experience.
              </p>
            </div>

            {/* Role options */}
            <div className="flex flex-col gap-3 mb-6">
              {ROLE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSelected(opt.value)}
                  className={cn(
                    'flex items-start gap-4 w-full rounded-2xl border-2 p-4 text-left transition-all duration-150',
                    selected === opt.value
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-outline-variant/40 hover:border-outline-variant hover:bg-surface-low/60',
                  )}
                >
                  <div
                    className={cn(
                      'mt-0.5 w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors',
                      selected === opt.value
                        ? 'bg-primary/15 text-primary'
                        : 'bg-surface-low text-on-surface-variant',
                    )}
                  >
                    {opt.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-on-surface">
                        {opt.label}
                      </span>
                      {selected === opt.value && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-4 h-4 rounded-full bg-primary flex items-center justify-center flex-shrink-0"
                        >
                          <svg
                            viewBox="0 0 12 12"
                            fill="none"
                            stroke="white"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-2.5 h-2.5"
                          >
                            <polyline points="2,6 5,9 10,3" />
                          </svg>
                        </motion.div>
                      )}
                    </div>
                    <p className="text-xs text-on-surface-variant mt-0.5 leading-relaxed">
                      {opt.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {error && (
              <p className="text-xs text-status-failed mb-4 text-center">{error}</p>
            )}

            <button
              type="button"
              onClick={handleContinue}
              disabled={!selected || busy}
              className="w-full py-3 rounded-2xl bg-primary text-white text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none inline-flex items-center justify-center gap-2"
            >
              {busy && (
                <svg
                  className="w-4 h-4 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="30 70"
                  />
                </svg>
              )}
              {busy ? 'Setting up your account…' : 'Continue'}
            </button>

            <p className="text-[11px] text-on-surface-variant/60 text-center mt-3">
              You can explore partner features later from your profile settings.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
